"use client"

import {
  addMinutes,
  format,
  interval,
  isBefore,
  isEqual,
  max as fnsMax,
  min as fnsMin,
  parse,
} from "date-fns"
import type { FC } from "react"
import { useEffect, useRef, useState } from "react"
import type { FieldError } from "react-hook-form"

import { HintText, Input } from "@/components/ui/input"
import type { InputBaseProps } from "@/components/ui/input"

interface ITimePickerProps extends InputBaseProps {
  value: string
  onChange?: (value: string) => void
  isDisabled?: boolean
  min?: string
  max?: string
  step?: number //in minutes
  window?: {
    time: string
    step: number // in minutes
  }
  error?: FieldError
}

const TimePicker: FC<ITimePickerProps> = ({
  value,
  onChange,
  isDisabled,
  min = "08:00",
  max = "17:00",
  step = 30,
  window,
  error,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedTime, setSelectedTime] = useState(value)

  const defaultTimeSelectRef = useRef<HTMLButtonElement>(null)

  const timeLimit =
    window && value
      ? interval(
          fnsMax([
            parse(min, "HH:mm", new Date()),
            addMinutes(parse(window.time, "HH:mm", new Date()), -window.step),
          ]),
          fnsMin([
            parse(max, "HH:mm", new Date()),
            addMinutes(parse(window.time, "HH:mm", new Date()), window.step),
          ]),
        )
      : interval(
          parse(min, "HH:mm", new Date()),
          parse(max, "HH:mm", new Date()),
        )

  const timeDropdownOptions: string[] = []
  let pointerTime = timeLimit.start
  while (
    isBefore(pointerTime, timeLimit.end) ||
    isEqual(pointerTime, timeLimit.end)
  ) {
    timeDropdownOptions.push(format(pointerTime, "HH:mm"))
    pointerTime = addMinutes(pointerTime, step)
  }

  useEffect(() => {
    if (isOpen && defaultTimeSelectRef.current) {
      defaultTimeSelectRef.current.scrollIntoView({
        block: "center",
        behavior: "smooth",
      })
    }
  }, [isOpen])

  const handleTimeChange = (time: string) => {
    setSelectedTime(time)
    onChange?.(time)
  }

  return (
    <div className="relative w-fit">
      <div
        className="relative flex w-fit items-center"
        onClick={() => !isDisabled && setIsOpen(true)}
      >
        <Input
          value={selectedTime}
          onChange={handleTimeChange}
          type="time"
          isDisabled={isDisabled}
          {...props}
        />
      </div>
      {isOpen && (
        <div className="bg-primary ring-secondary absolute z-20 mt-1 w-full rounded-lg py-1 shadow-lg ring-1">
          <div
            className="[&::-webkit-scrollbar-thumb]:bg-quaternary max-h-40 overflow-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "var(--color-stone-400) transparent",
            }}
          >
            {timeDropdownOptions.map((time) => (
              <button
                key={time}
                type="button"
                ref={time === window?.time ? defaultTimeSelectRef : null}
                onClick={() => {
                  handleTimeChange(time)
                  setIsOpen(false)
                }}
                className="text-primary hover:bg-primary_hover w-full px-3 py-2 text-left text-sm"
              >
                {time}
              </button>
            ))}
          </div>
        </div>
      )}
      {error && <HintText isInvalid>{error.message}</HintText>}
    </div>
  )
}

export default TimePicker
